import React, { useState } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold,
  Italic,
  Strikethrough,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Link2,
  Table as TableIcon,
  Undo,
  Redo,
  Eye,
  Code2,
} from 'lucide-react';
import './tiptap-editor.css';

interface TiptapEmailEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  showVariables?: boolean;
}

const TiptapEmailEditor: React.FC<TiptapEmailEditorProps> = ({
  value,
  onChange,
  placeholder = 'Start writing your email content...',
  showVariables = true,
}) => {
  const [showHtmlView, setShowHtmlView] = useState(false);
  const [htmlCode, setHtmlCode] = useState(value);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          style: 'color: #3b82f6; text-decoration: underline;',
        },
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          style: 'border-collapse: collapse; width: 100%;',
        },
      }),
      TableRow,
      TableCell.configure({
        HTMLAttributes: {
          style: 'border: 1px solid #d1d5db; padding: 8px;',
        },
      }),
      TableHeader.configure({
        HTMLAttributes: {
          style: 'border: 1px solid #d1d5db; padding: 8px; background-color: #f3f4f6; font-weight: bold;',
        },
      }),
      TextStyle,
      Color,
      Underline,
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: value,
    onUpdate: ({ editor }: { editor: Editor }) => {
      const html = editor.getHTML();
      onChange(html);
      setHtmlCode(html);
    },
  });

  // Sync external value changes
  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
      setHtmlCode(value);
    }
  }, [value, editor]);

  const toggleHtmlView = () => {
    if (showHtmlView) {
      // Switching from HTML to visual: update editor content
      editor?.commands.setContent(htmlCode);
      onChange(htmlCode);
    }
    setShowHtmlView(!showHtmlView);
  };

  const handleHtmlCodeChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newHtml = e.target.value;
    setHtmlCode(newHtml);
    onChange(newHtml);
  };

  const setLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) {
      editor?.chain().focus().setLink({ href: url }).run();
    }
  };

  const addTable = () => {
    editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  if (!editor) {
    return <div>Loading editor...</div>;
  }

  return (
    <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden bg-white dark:bg-gray-700">
      {/* Toolbar */}
      <div className="border-b border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 p-2">
        <div className="flex flex-wrap gap-1 items-center">
          {/* View Toggle */}
          <button
            type="button"
            onClick={toggleHtmlView}
            className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
              showHtmlView ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300' : ''
            }`}
            title={showHtmlView ? 'Visual Editor' : 'HTML Source'}
          >
            {showHtmlView ? <Eye className="w-4 h-4" /> : <Code2 className="w-4 h-4" />}
          </button>

          <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />

          {!showHtmlView && (
            <>
              {/* Text Formatting */}
              <button
                type="button"
                onClick={() => editor.chain().focus().toggleBold().run()}
                className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
                  editor.isActive('bold') ? 'bg-gray-200 dark:bg-gray-700' : ''
                }`}
                title="Bold"
              >
                <Bold className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => editor.chain().focus().toggleItalic().run()}
                className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
                  editor.isActive('italic') ? 'bg-gray-200 dark:bg-gray-700' : ''
                }`}
                title="Italic"
              >
                <Italic className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
                  editor.isActive('underline') ? 'bg-gray-200 dark:bg-gray-700' : ''
                }`}
                title="Underline"
              >
                <span className="w-4 h-4 flex items-center justify-center text-xs font-bold">U</span>
              </button>
              <button
                type="button"
                onClick={() => editor.chain().focus().toggleStrike().run()}
                className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
                  editor.isActive('strike') ? 'bg-gray-200 dark:bg-gray-700' : ''
                }`}
                title="Strikethrough"
              >
                <Strikethrough className="w-4 h-4" />
              </button>

              <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />

              {/* Headings */}
              <select
                onChange={(e) => {
                  const level = parseInt(e.target.value);
                  if (level === 0) {
                    editor.chain().focus().setParagraph().run();
                  } else {
                    editor.chain().focus().toggleHeading({ level: level as any }).run();
                  }
                }}
                className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                value={
                  editor.isActive('heading', { level: 1 })
                    ? 1
                    : editor.isActive('heading', { level: 2 })
                    ? 2
                    : editor.isActive('heading', { level: 3 })
                    ? 3
                    : 0
                }
              >
                <option value="0">Paragraph</option>
                <option value="1">Heading 1</option>
                <option value="2">Heading 2</option>
                <option value="3">Heading 3</option>
              </select>

              <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />

              {/* Text Alignment */}
              <button
                type="button"
                onClick={() => editor.chain().focus().setTextAlign('left').run()}
                className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
                  editor.isActive({ textAlign: 'left' }) ? 'bg-gray-200 dark:bg-gray-700' : ''
                }`}
                title="Align Left"
              >
                <AlignLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => editor.chain().focus().setTextAlign('center').run()}
                className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
                  editor.isActive({ textAlign: 'center' }) ? 'bg-gray-200 dark:bg-gray-700' : ''
                }`}
                title="Align Center"
              >
                <AlignCenter className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => editor.chain().focus().setTextAlign('right').run()}
                className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
                  editor.isActive({ textAlign: 'right' }) ? 'bg-gray-200 dark:bg-gray-700' : ''
                }`}
                title="Align Right"
              >
                <AlignRight className="w-4 h-4" />
              </button>

              <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />

              {/* Lists */}
              <button
                type="button"
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
                  editor.isActive('bulletList') ? 'bg-gray-200 dark:bg-gray-700' : ''
                }`}
                title="Bullet List"
              >
                <List className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
                  editor.isActive('orderedList') ? 'bg-gray-200 dark:bg-gray-700' : ''
                }`}
                title="Numbered List"
              >
                <ListOrdered className="w-4 h-4" />
              </button>

              <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />

              {/* Link & Table */}
              <button
                type="button"
                onClick={setLink}
                className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
                  editor.isActive('link') ? 'bg-gray-200 dark:bg-gray-700' : ''
                }`}
                title="Insert Link"
              >
                <Link2 className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={addTable}
                className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                title="Insert Table"
              >
                <TableIcon className="w-4 h-4" />
              </button>

              <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />

              {/* Text Color */}
              <input
                type="color"
                onInput={(e: any) => editor.chain().focus().setColor(e.target.value).run()}
                value={editor.getAttributes('textStyle').color || '#000000'}
                className="w-8 h-8 rounded cursor-pointer"
                title="Text Color"
              />

              <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />

              {/* Undo/Redo */}
              <button
                type="button"
                onClick={() => editor.chain().focus().undo().run()}
                disabled={!editor.can().undo()}
                className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Undo"
              >
                <Undo className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => editor.chain().focus().redo().run()}
                disabled={!editor.can().redo()}
                className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Redo"
              >
                <Redo className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Editor Content */}
      <div className="relative">
        {showHtmlView ? (
          <textarea
            value={htmlCode}
            onChange={handleHtmlCodeChange}
            className="w-full p-4 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm focus:outline-none"
            rows={20}
            style={{ resize: 'vertical' }}
          />
        ) : (
          <EditorContent
            editor={editor}
            className="prose prose-sm max-w-none p-4 min-h-[300px] focus:outline-none dark:prose-invert"
          />
        )}
      </div>

      {/* Helper Text + Variable Chips */}
      {showVariables && <div className="border-t border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 px-4 py-3">
        <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
          Click to insert variable:
        </p>
        <div className="flex flex-wrap gap-1.5">
          {[
            { label: 'Lead Name', variable: '{{lead.name}}' },
            { label: 'Lead Email', variable: '{{lead.email}}' },
            { label: 'Lead Phone', variable: '{{lead.phone}}' },
            { label: 'Lead Status', variable: '{{lead.status}}' },
            { label: 'Company Name', variable: '{{client.company_name}}' },
          ].map(({ label, variable }) => (
            <button
              key={variable}
              type="button"
              onClick={() => {
                if (showHtmlView) {
                  // In HTML mode we can't insert at cursor easily; just notify user
                  navigator.clipboard?.writeText(variable);
                  return;
                }
                editor?.chain().focus().insertContent(variable).run();
              }}
              title={`Insert ${variable}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-mono hover:bg-blue-200 dark:hover:bg-blue-800/60 transition-colors cursor-pointer border border-blue-200 dark:border-blue-700"
            >
              <span className="text-blue-400 dark:text-blue-500 text-[10px]">{'{'+'{'}</span>
              {label}
              <span className="text-blue-400 dark:text-blue-500 text-[10px]">{'}'+'}'}</span>
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          Toggle <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">{'<>'}</code> to edit raw HTML source.
        </p>
      </div>}
    </div>
  );
};

export default TiptapEmailEditor;
